import { AlertCircle, ArrowDown, ArrowUp } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Spinner } from "~/components/ui/spinner";

import { TIER_DESCRIPTIONS, TIER_LABELS, type TierInfo } from "./types";

function TierOption({
  label,
  description,
  isActive,
  children,
}: {
  label: string;
  description: string;
  isActive: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${isActive ? "border-primary bg-primary/5" : ""}`}
    >
      <h3 className="font-semibold">{label}</h3>
      <p className="text-muted-foreground mt-1 text-sm">{description}</p>
      {isActive ? (
        <Badge className="mt-3" variant="outline">
          Current Plan
        </Badge>
      ) : (
        children
      )}
    </div>
  );
}

export function ChangePlanCard({
  currentTier,
  tierInfo,
  isUpgrading,
  isDowngrading,
  showUpgradeConfirm,
  showDowngradeConfirm,
  setShowUpgradeConfirm,
  setShowDowngradeConfirm,
  onUpgrade,
  onDowngrade,
}: {
  currentTier: string;
  tierInfo: TierInfo;
  isUpgrading: boolean;
  isDowngrading: boolean;
  showUpgradeConfirm: "growth" | "enterprise" | null;
  showDowngradeConfirm: boolean;
  setShowUpgradeConfirm: (tier: "growth" | "enterprise" | null) => void;
  setShowDowngradeConfirm: (show: boolean) => void;
  onUpgrade: (tier: "growth" | "enterprise") => Promise<void>;
  onDowngrade: () => Promise<void>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Plan</CardTitle>
        <CardDescription>
          Upgrade for lower processing fees or downgrade to the free plan
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TierOption
            description={TIER_DESCRIPTIONS.free}
            isActive={currentTier === "free"}
            label="Free"
          >
            {currentTier !== "free" && (
              <Button
                className="mt-3 w-full"
                disabled={isDowngrading}
                onClick={() => setShowDowngradeConfirm(true)}
                size="sm"
                variant="outline"
              >
                <ArrowDown className="mr-2 size-4" />
                Downgrade
              </Button>
            )}
          </TierOption>

          <TierOption
            description={TIER_DESCRIPTIONS.growth}
            isActive={currentTier === "growth"}
            label="Growth"
          >
            {currentTier !== "growth" && (
              <Button
                className="mt-3 w-full"
                disabled={isUpgrading}
                onClick={() => setShowUpgradeConfirm("growth")}
                size="sm"
                variant={currentTier === "free" ? "default" : "outline"}
              >
                {currentTier === "free" ? (
                  <>
                    <ArrowUp className="mr-2 size-4" />
                    Upgrade
                  </>
                ) : (
                  <>
                    <ArrowDown className="mr-2 size-4" />
                    Downgrade
                  </>
                )}
              </Button>
            )}
          </TierOption>

          <TierOption
            description={TIER_DESCRIPTIONS.enterprise}
            isActive={currentTier === "enterprise"}
            label="Enterprise"
          >
            {currentTier !== "enterprise" && (
              <Button
                className="mt-3 w-full"
                disabled={isUpgrading}
                onClick={() => setShowUpgradeConfirm("enterprise")}
                size="sm"
              >
                <ArrowUp className="mr-2 size-4" />
                Upgrade
              </Button>
            )}
          </TierOption>
        </div>

        {showUpgradeConfirm && (
          <div className="bg-muted/50 mt-4 rounded-lg border p-4">
            <p className="font-medium">
              Upgrade to {TIER_LABELS[showUpgradeConfirm]}?
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              Your subscription will be updated to{" "}
              {TIER_DESCRIPTIONS[showUpgradeConfirm]}. You'll be billed based on
              your {tierInfo.locationCount} location
              {tierInfo.locationCount !== 1 ? "s" : ""}.
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                disabled={isUpgrading}
                onClick={() => onUpgrade(showUpgradeConfirm)}
                size="sm"
              >
                {isUpgrading ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Upgrading...
                  </>
                ) : (
                  "Confirm Upgrade"
                )}
              </Button>
              <Button
                disabled={isUpgrading}
                onClick={() => setShowUpgradeConfirm(null)}
                size="sm"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {showDowngradeConfirm && (
          <div className="border-destructive bg-destructive/5 mt-4 rounded-lg border p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-destructive mt-0.5 size-5" />
              <div className="flex-1">
                <p className="font-medium">Downgrade to Free?</p>
                <p className="text-muted-foreground mt-1 text-sm">
                  Your subscription will be canceled at the end of the current
                  billing period. You'll move to 4% processing fees with no
                  monthly cost.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    disabled={isDowngrading}
                    onClick={onDowngrade}
                    size="sm"
                    variant="destructive"
                  >
                    {isDowngrading ? (
                      <>
                        <Spinner className="mr-2 size-4" />
                        Scheduling...
                      </>
                    ) : (
                      "Confirm Downgrade"
                    )}
                  </Button>
                  <Button
                    disabled={isDowngrading}
                    onClick={() => setShowDowngradeConfirm(false)}
                    size="sm"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
